import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Typography,
  Box,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  TableContainer,
  TableSortLabel,
  TablePagination,
  IconButton,
  Collapse,
  Chip,
  Paper,
} from '@material-ui/core';
import axios from 'axios';
import envConfig from '../../bin/env.config';
import { useSelector } from 'react-redux';
import {
  STATUS_OPTIONS,
  STATUS_COLORS,
  BACKGROUND_STATUS_COLORS,
} from '../../utility/statusConfig';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import StalePriceReportLayout, { useStalePriceReportStyles } from './CustomerReport/StalePriceReportLayout';
import StalePriceStoreDetailTable, {
  recomputeStalePriceSummaryFromDetails,
} from './CustomerReport/StalePriceStoreDetailTable';
import {
  formatDateTime,
  getSortValue,
  DEFAULT_FILTERS,
  REVIEW_CHIP_STYLES,
  parseStalePriceFiltersFromSearch,
  serializeStalePriceFiltersToSearch,
} from './CustomerReport/stalePriceReportConfig';

const SUMMARY_COLUMNS = [
  { label: 'Banner', sortKey: 'banner_name', type: 'string' },
  { label: 'PMGs', sortKey: 'pmgs_reported', type: 'number' },
  { label: 'Stores', sortKey: 'stores_reported', type: 'number' },
  { label: 'User Reports', sortKey: 'total_user_reports', type: 'number' },
  { label: 'Review', sortKey: 'reviewed_stores', type: 'number' },
  { label: 'Status Breakdown', sortKey: null },
  { label: 'Latest Report', sortKey: 'latest_reported_at', type: 'date' },
];

const STATUS_BREAKDOWN_EXPORT_IDS = [1, 2, 3, 4, 5];

export default function CompanyReportToolStalePriceReportsByBannerPage(props) {
  const history = useHistory();
  const classes = useStalePriceReportStyles();
  const token = useSelector((state) => state.session.token);
  const user_type_id = props.match.params.user_type_id;
  const project_id = props.match.params.project_id;

  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [saveSnackbar, setSaveSnackbar] = useState('');
  const [filters, setFilters] = useState(() =>
    parseStalePriceFiltersFromSearch(props.location.search)
  );
  const [orderBy, setOrderBy] = useState('needs_review_stores');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedBannerId, setExpandedBannerId] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const syncFiltersToUrl = useCallback(
    (nextFilters) => {
      const search = serializeStalePriceFiltersToSearch(nextFilters);
      history.replace({
        pathname: props.location.pathname,
        search,
      });
    },
    [history, props.location.pathname]
  );

  const handleFilterChange = useCallback(
    (nextFilters) => {
      setFilters(nextFilters);
      setPage(0);
      syncFiltersToUrl(nextFilters);
    },
    [syncFiltersToUrl]
  );

  const handleClearAllFilters = useCallback(() => {
    handleFilterChange({ ...DEFAULT_FILTERS });
  }, [handleFilterChange]);

  useEffect(() => {
    if (!filtersInitialized) {
      const parsed = parseStalePriceFiltersFromSearch(props.location.search);
      setFilters(parsed);
      setFiltersInitialized(true);
    }
  }, [props.location.search, filtersInitialized]);

  const reporterFilterParams = useMemo(
    () => ({
      user_type_id,
      city: filters.city,
      banners: filters.banners,
      report_type: filters.reportType,
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined,
    }),
    [user_type_id, filters.city, filters.banners, filters.reportType, filters.fromDate, filters.toDate]
  );

  const filterParams = useMemo(
    () => ({
      ...reporterFilterParams,
      reported_by: filters.reporter || undefined,
    }),
    [reporterFilterParams, filters.reporter]
  );

  const [reporterOptions, setReporterOptions] = useState([]);

  const fetchReporters = async () => {
    try {
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/srp-pricing-tool/stale-price-reports-reporters`,
        reporterFilterParams,
        { headers: { Authorization: token } }
      );
      const reporters = (response.data.data || []).map((reporter) => {
        const email = (reporter.reporter_email || '').trim();
        return {
          value: String(reporter.reported_by),
          label: email || `User #${reporter.reported_by}`,
        };
      });
      setReporterOptions(reporters);
    } catch (error) {
      console.error('Error fetching stale price reporters:', error);
    }
  };

  const fetchSummaries = async () => {
    try {
      setLoading(true);
      setExpandedBannerId(null);
      setDetailsCache({});
      setDetailsLoading({});
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/srp-pricing-tool/stale-price-reports-banner-summary`,
        filterParams,
        { headers: { Authorization: token } }
      );
      setSummaries(response.data.data || []);
    } catch (error) {
      console.error('Error fetching stale price banner summaries:', error);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = async (bannerId) => {
    if (expandedBannerId === bannerId) {
      setExpandedBannerId(null);
      return;
    }
    setExpandedBannerId(bannerId);

    if (detailsCache[bannerId] || detailsLoading[bannerId]) {
      return;
    }

    setDetailsLoading((prev) => ({ ...prev, [bannerId]: true }));
    try {
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/srp-pricing-tool/stale-price-reports-banner-details`,
        { ...filterParams, company_id: bannerId },
        { headers: { Authorization: token } }
      );
      setDetailsCache((prev) => ({ ...prev, [bannerId]: response.data.data || [] }));
    } catch (error) {
      console.error('Error fetching stale price banner details:', error);
      setShowError(true);
    } finally {
      setDetailsLoading((prev) => ({ ...prev, [bannerId]: false }));
    }
  };

  useEffect(() => {
    if (user_type_id && filtersInitialized) {
      fetchReporters();
    }
  }, [reporterFilterParams, user_type_id, filtersInitialized]);

  useEffect(() => {
    if (user_type_id && filtersInitialized) {
      fetchSummaries();
    }
  }, [filterParams, user_type_id, filtersInitialized]);

  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.review, filters.reporter, orderBy, order]);

  const handleStatusSaved = (statusData, reportContext) => {
    if (!reportContext || expandedBannerId == null) return;

    const bannerId = expandedBannerId;
    const statusId = Number(statusData.status_id);
    const updatedDetail = {
      ...reportContext,
      manual_check_status_id: statusId,
      manual_check_edited_at: statusData.edited_at || new Date().toISOString(),
      manual_check_edited_by: statusData.edited_by || reportContext.manual_check_edited_by,
      has_valid_manual_check: true,
    };

    setDetailsCache((prev) => {
      const currentDetails = prev[bannerId] || [];
      const nextDetails = currentDetails.map((detail) =>
        detail.sighting_id === reportContext.sighting_id ? updatedDetail : detail
      );

      setSummaries((prevSummaries) =>
        prevSummaries.map((summary) => {
          if (summary.company_id !== bannerId) {
            return summary;
          }
          return recomputeStalePriceSummaryFromDetails(summary, nextDetails);
        })
      );

      return { ...prev, [bannerId]: nextDetails };
    });

    setSaveSnackbar('Status saved');
  };

  const filteredSummaries = useMemo(() => {
    let result = summaries;

    if (filters.review === 'needs_review') {
      result = result.filter((row) => row.needs_review_stores > 0);
    } else if (filters.review === 'reviewed') {
      result = result.filter((row) => row.needs_review_stores === 0);
    }

    if (!filters.search) return result;
    const lowerFilter = filters.search.toLowerCase();
    return result.filter((row) =>
      (row.banner_name || '').toLowerCase().includes(lowerFilter)
    );
  }, [summaries, filters.search, filters.review]);

  const sortedSummaries = useMemo(() => {
    const column = SUMMARY_COLUMNS.find((col) => col.sortKey === orderBy);
    const type = column?.type || 'string';
    const comparator = (a, b) => {
      const aVal = getSortValue(a, orderBy, type);
      const bVal = getSortValue(b, orderBy, type);
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    };
    return [...filteredSummaries].sort(comparator);
  }, [filteredSummaries, orderBy, order]);

  const paginatedSummaries = useMemo(
    () => sortedSummaries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedSummaries, page, rowsPerPage]
  );

  const needsReviewCount = useMemo(
    () => filteredSummaries.filter((row) => row.needs_review_stores > 0).length,
    [filteredSummaries]
  );
  const reviewedCount = useMemo(
    () => filteredSummaries.filter((row) => row.needs_review_stores === 0).length,
    [filteredSummaries]
  );

  const handleSort = (sortKey) => {
    const isAsc = orderBy === sortKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(sortKey);
  };

  const handleExportCSV = () => {
    const headers = [
      'Banner ID',
      'Banner Name',
      'Items Reported',
      'Stores Reported',
      'Reviewed Stores',
      'Needs Review Stores',
      'Review %',
      ...STATUS_BREAKDOWN_EXPORT_IDS.map((id) => STATUS_OPTIONS[id]),
      'Latest Report',
    ];

    let csv = headers.join(',') + '\n';
    sortedSummaries.forEach((row) => {
      const rowData = [
        row.company_id,
        `"${(row.banner_name || '').replace(/"/g, '""')}"`,
        row.pmgs_reported,
        row.stores_reported,
        row.reviewed_stores,
        row.needs_review_stores,
        row.review_pct,
        ...STATUS_BREAKDOWN_EXPORT_IDS.map((id) => row.status_counts?.[id] ?? 0),
        `"${formatDateTime(row.latest_reported_at).replace(/"/g, '""')}"`,
      ];
      csv += rowData.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stale_price_reports_by_banner.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const renderStatusBreakdown = (statusCounts) => {
    const entries = Object.entries(statusCounts || {});
    if (entries.length === 0) {
      return <Typography variant="caption" color="textSecondary">—</Typography>;
    }
    return (
      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
        {entries.map(([statusId, count]) => (
          <Chip
            key={statusId}
            size="small"
            label={`${STATUS_OPTIONS[statusId] || statusId}: ${count}`}
            style={{
              color: STATUS_COLORS[statusId],
              backgroundColor: BACKGROUND_STATUS_COLORS[statusId],
            }}
          />
        ))}
      </Box>
    );
  };

  const showEmptyState = !loading && sortedSummaries.length === 0;

  return (
    <>
      <StalePriceReportLayout
        view="byBanner"
        title="Stale Price Reports"
        userTypeId={user_type_id}
        projectId={project_id}
        token={token}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        reporterOptions={reporterOptions}
        countLabel="Banners"
        totalCount={filteredSummaries.length}
        needsReviewCount={needsReviewCount}
        reviewedCount={reviewedCount}
        loading={loading}
        onExport={handleExportCSV}
        showEmptyState={showEmptyState}
        emptyMessage="No banner summaries found."
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ backgroundColor: '#f5f5f5', width: 120 }} />
                    {SUMMARY_COLUMNS.map((col) => (
                      <TableCell
                        key={col.label}
                        style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                      >
                        {col.sortKey ? (
                          <TableSortLabel
                            active={orderBy === col.sortKey}
                            direction={orderBy === col.sortKey ? order : 'asc'}
                            onClick={() => handleSort(col.sortKey)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSummaries.map((row) => {
                    const bannerId = row.company_id;
                    const isExpanded = expandedBannerId === bannerId;
                    return (
                      <React.Fragment key={bannerId}>
                        <TableRow
                          className={
                            row.needs_review_stores > 0
                              ? `${classes.tableRowHover} ${classes.needsReviewRow}`
                              : classes.tableRowHover
                          }
                          style={
                            isExpanded ? { borderLeft: '3px solid #1976d2' } : undefined
                          }
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => handleToggleExpand(bannerId)}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                              <Typography variant="caption" color="textSecondary">
                                {row.stores_reported} store{row.stores_reported !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell style={{ minWidth: 200 }}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {row.banner_name}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.pmgs_reported}</TableCell>
                          <TableCell>{row.stores_reported}</TableCell>
                          <TableCell>{row.total_user_reports}</TableCell>
                          <TableCell style={{ minWidth: 160 }}>
                            <Chip
                              size="small"
                              label={`Reviewed: ${row.reviewed_stores}`}
                              style={{ ...REVIEW_CHIP_STYLES.reviewed, marginRight: 4 }}
                            />
                            <Chip
                              size="small"
                              label={`Needs: ${row.needs_review_stores}`}
                              style={{ ...REVIEW_CHIP_STYLES.needsReview, marginRight: 4 }}
                            />
                            <Typography variant="caption" display="block">
                              {row.review_pct}%
                            </Typography>
                          </TableCell>
                          <TableCell style={{ minWidth: 180 }}>
                            {renderStatusBreakdown(row.status_counts)}
                          </TableCell>
                          <TableCell>{formatDateTime(row.latest_reported_at)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={SUMMARY_COLUMNS.length + 1}
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box margin={1}>
                                <Paper
                                  variant="outlined"
                                  style={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}
                                >
                                  <StalePriceStoreDetailTable
                                    details={detailsCache[bannerId]}
                                    loading={detailsLoading[bannerId]}
                                    onStatusSaved={handleStatusSaved}
                                    onClearFilters={handleClearAllFilters}
                                  />
                                </Paper>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {sortedSummaries.length > 0 && (
              <TablePagination
                component="div"
                count={sortedSummaries.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Grid>
        </Grid>
      </StalePriceReportLayout>

      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        message="Failed to load stale price banner data."
      />
      <Snackbar
        open={Boolean(saveSnackbar)}
        autoHideDuration={2000}
        onClose={() => setSaveSnackbar('')}
        message={saveSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
